/**
 * Market rules for the EEC energy label.
 *
 * Each constant below is a **separate axis**. They all happen to equal France today, which is a
 * coincidence of the current market set, not a shared rule — a third market will need an arbitrary
 * subset of them, so they must not be collapsed into one. Energy-label regulation differs per
 * country in what is disclosed, for which vehicles, and in what form; these axes are the parts we
 * actually vary on.
 *
 * Note the axes are keyed by **different standards**, deliberately: an axis keyed to a URL path
 * segment uses the ISO2 country the URL itself uses, while axes keyed to our own market concept use
 * `IMarket` (ISO3). The ISO3 ones are typed so the compiler rejects a code from the wrong standard.
 *
 * When a third market lands needing a different combination, fold these into a single
 * `Record<IMarket, IEnergyLabelMarketRule>` table. That is cheap at that point and speculative now.
 */
import type { IMarket } from 'data/schema';

/**
 * Markets whose energy label is sourced from the VTP per-car detail resource. (ISO3)
 *
 * Outside these markets the VTP detail hop is skipped entirely and its energy fields are never
 * merged: the class feeds the legally mandated ENVKV consumption text in DEU/CHE, so a VTP-supplied
 * value must not reach it.
 */
export const EEC_LABEL_MARKETS: ReadonlySet<IMarket> = new Set<IMarket>(['FRA']);

/**
 * Countries whose label URL carries a trailing CO2 (g/km) segment: `/fr/fr-fr/C/128`. (ISO2)
 *
 * ISO2 because it keys the URL path segment itself, which is ISO2 — this is not an inconsistency to
 * be normalised away.
 *
 * Verified live 2026-07-30: given the three-segment form, MediaService answers **HTTP 200** with a
 * generic placeholder rendering "A — 0 g CO2/km", byte-identical for every class. It therefore
 * passes both an existence check and an image-dimension check while showing a legally mandated
 * disclosure that is wrong. A URL for these countries is either complete or must not be built.
 */
const CO2_SEGMENT_COUNTRIES: ReadonlySet<string> = new Set(['fr']);

/**
 * Markets that withhold the energy label for used stock. (ISO3)
 *
 * For used cars the label is not legally required and neither the efficiency class nor the CO2
 * figure can be guaranteed present or valid, so the French Audi team asked for it to be hidden
 * outright rather than shown on unreliable data.
 *
 * Safe to extend to any market: this axis withholds the **label image only**. It deliberately does
 * not strip `efficiencyClass` from the vehicle data, because that field also feeds the legally
 * mandated ENVKV consumption text in DEU/CHE — removing it there would not hide a label, it would
 * fail `isWltpDataValid` and reject the whole vehicle with a 400.
 */
export const USED_CAR_LABEL_SUPPRESSED_MARKETS: ReadonlySet<IMarket> = new Set<IMarket>(['FRA']);

export function requiresCo2Segment(country: string): boolean {
  return CO2_SEGMENT_COUNTRIES.has(country.toLowerCase());
}

/**
 * True when the energy label must be withheld for this vehicle.
 *
 * Applies only to labels we derive ourselves. An integrator-supplied URL is a contractual override
 * from the embedding partner and keeps precedence — the concern here is that *our* used-car data is
 * untrustworthy, not that displaying a label would be unlawful.
 */
export function isEnergyLabelSuppressed(market: IMarket | undefined, isUsedCar: boolean | undefined): boolean {
  if (!market || !isUsedCar) return false;
  return USED_CAR_LABEL_SUPPRESSED_MARKETS.has(market);
}
